import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Search, Phone, Mail, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface Contact {
  id: string;
  nome: string;
  cargo: string | null;
  setor: string | null;
  email: string | null;
  telefone: string | null;
  ramal: string | null;
  localizacao: string | null;
  tipo: string;
  ativo: boolean;
  created_at: string;
}

export const ContactsManager: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    cargo: '',
    setor: '',
    email: '',
    telefone: '',
    ramal: '',
    localizacao: '',
    tipo: 'equipe'
  });

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = contacts.filter(c =>
        c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.setor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.telefone?.includes(searchTerm)
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchTerm, contacts]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      if (editingContact) {
        const { error } = await supabase
          .from('contatos')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingContact.id);

        if (error) throw error;
        toast.success('Contato atualizado com sucesso');
      } else {
        const { error } = await supabase
          .from('contatos')
          .insert([formData]);

        if (error) throw error;
        toast.success('Contato adicionado com sucesso');
      }

      setShowModal(false);
      setEditingContact(null);
      resetForm();
      loadContacts();
    } catch (error: any) {
      console.error('Error saving contact:', error);
      toast.error('Erro ao salvar contato: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      nome: contact.nome,
      cargo: contact.cargo || '',
      setor: contact.setor || '',
      email: contact.email || '',
      telefone: contact.telefone || '',
      ramal: contact.ramal || '',
      localizacao: contact.localizacao || '',
      tipo: contact.tipo
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja remover este contato?')) return;

    try {
      const { error } = await supabase
        .from('contatos')
        .update({ ativo: false })
        .eq('id', id);

      if (error) throw error;
      toast.success('Contato removido com sucesso');
      loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Erro ao remover contato');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cargo: '',
      setor: '',
      email: '',
      telefone: '',
      ramal: '',
      localizacao: '',
      tipo: 'equipe'
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
    resetForm();
  };

  // Base de inputs com texto preto e placeholder visível
  const inputBase =
    "w-full px-3 py-2 border border-slate-300 rounded-lg " +
    "text-slate-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-gray-400 " +
    "focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-700";

  return (
    <div className="space-y-6 text-slate-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-gray-100">Gerenciar Contatos</h2>

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition font-semibold"
        >
          <Plus className="w-5 h-5" />
          <span>Novo Contato</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-slate-200 dark:border-gray-700">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-700 dark:text-gray-300" />
          <input
            type="text"
            placeholder="Buscar por nome, cargo, setor, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 ${inputBase}`}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-slate-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Setor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-900 dark:text-gray-100 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-slate-200">
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50/70">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-blue-800" />
                        </div>
                        <div className="text-sm font-bold text-slate-900 dark:text-gray-100">
                          {contact.nome}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-gray-100">
                      {contact.cargo || '-'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-gray-100">
                      {contact.setor || '-'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900 dark:text-gray-100 space-y-1">
                        {contact.email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="w-3.5 h-3.5 text-slate-900 dark:text-gray-100" />
                            <span className="text-slate-900 dark:text-gray-100">{contact.email}</span>
                          </div>
                        )}

                        {contact.telefone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-3.5 h-3.5 text-slate-900 dark:text-gray-100" />
                            <span className="text-slate-900 dark:text-gray-100">{contact.telefone}</span>
                          </div>
                        )}

                        {contact.ramal && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-3.5 h-3.5 text-slate-900 dark:text-gray-100" />
                            <span className="text-slate-900 dark:text-gray-100">Ramal: {contact.ramal}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded-full ${
                          contact.tipo === 'representante'
                            ? 'bg-purple-100 text-purple-950'
                            : 'bg-emerald-100 text-emerald-950'
                        }`}
                      >
                        {contact.tipo === 'representante' ? 'Representante' : 'Equipe'}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="text-blue-900 hover:text-blue-950 mr-3"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="text-red-800 hover:text-red-950"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredContacts.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-900 dark:text-gray-100">
              <User className="w-12 h-12 mx-auto mb-3 text-slate-700 dark:text-gray-300" />
              <p className="font-bold">Nenhum contato encontrado</p>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 mb-4">
                {editingContact ? 'Editar Contato' : 'Novo Contato'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Nome *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Cargo
                    </label>
                    <input
                      type="text"
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Setor
                    </label>
                    <input
                      type="text"
                      value={formData.setor}
                      onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Ramal
                    </label>
                    <input
                      type="text"
                      value={formData.ramal}
                      onChange={(e) => setFormData({ ...formData, ramal: e.target.value })}
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Localização
                    </label>
                    <input
                      type="text"
                      value={formData.localizacao}
                      onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
                      className={inputBase}
                      placeholder="ex: Apucarana - PR"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 dark:text-gray-100 mb-1">
                      Tipo
                    </label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className={inputBase}
                    >
                      <option value="equipe">Equipe</option>
                      <option value="representante">Representante</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-slate-900 dark:text-gray-100 bg-slate-100 rounded-lg hover:bg-slate-200 transition font-bold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition font-bold"
                  >
                    {editingContact ? 'Atualizar' : 'Adicionar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
